import { FeatureRequest, MondayDocument, MondayBlock } from "interfaces/feature-request";
import React, { useEffect } from "react";
import styled from "styled-components";
import styles from "styles/styles";
import isEmpty from "lodash/isEmpty";
import { EmptyStateHeader, TabHeader } from "../shared/generic";
import { useTranslate } from "hooks/common/useTranslate/useTranslate";
import useFetch from "hooks/common/useFetch/useFetch";
import LoadingWrapper from "../shared/LoadingWrapper";
import LoadingIndicator from "components/common/ui/LoadingIndicator/LoadingIndicator";

const DocContainer = styled.div`
    color: ${styles.colors.black};
`;

const LargeTitle = styled.h5`
    font-size: 18px;
    margin: 0.5rem 0 0.5rem 0;
`;

const SmallTitle = styled.h6`
    font-size: 18px;
    font-weight: 500;
    margin: 0.5rem 0 0.5rem 0;
`;

const NormalText = styled.p`
    font-size: 16px;
    margin: 0.5rem 0;
`;

const Image = styled.img<{ width?: number }>`
    max-width: 100%;
    height: auto;
    width: ${(props) => (props.width ? `${props.width}px` : "auto")};
    display: block;
    margin: 1rem 0;
`;

const Table = styled.table`
    border-collapse: collapse;

    td {
        border: 1px solid #ddd;
        padding: 0.75rem;
    }
`;

const groupCellsByRow = (cells: MondayBlock[]): MondayBlock[][] =>
    cells.reduce<MondayBlock[][]>(
        (rows, cell, idx) => {
            if (idx > 0 && cells[idx].position - cells[idx - 1].position === 131072) {
                rows.push([]);
            }

            rows[rows.length - 1].push(cell);

            return rows;
        },
        [[]]
    );

const getBlockContent = (block?: MondayBlock): string => block?.content || "\u00A0";

const renderBlock = (
    block: MondayBlock,
    _index: number,
    blocks: MondayBlock[]
): React.ReactNode => {
    switch (block.type) {
        case "large title":
            return <LargeTitle key={block.id}>{getBlockContent(block)}</LargeTitle>;

        case "small title":
            return <SmallTitle key={block.id}>{getBlockContent(block)}</SmallTitle>;

        case "image":
            return <Image key={block.id} src={block.content} />;

        case "bulleted list":
            return <NormalText key={block.id}>• {getBlockContent(block)}</NormalText>;

        case "normal text":
            const parentBlock = blocks.find((b) => b.id === block.parent_block_id);

            if (parentBlock?.type === "cell") {
                return null;
            }

            return <NormalText key={block.id}>{getBlockContent(block)}</NormalText>;

        case "check list": {
            return (
                <NormalText key={block.id}>
                    {block.checked ? "☑" : "☐"} {getBlockContent(block)}
                </NormalText>
            );
        }

        case "table": {
            const cells = blocks.filter((b) => b.parent_block_id === block.id);

            return (
                <Table key={block.id}>
                    <tbody>
                        {groupCellsByRow(cells).map((row, rowIdx) => (
                            <tr key={rowIdx}>
                                {row.map((cell) => (
                                    <td key={cell.id}>
                                        <NormalText>
                                            {getBlockContent(
                                                blocks.find((b) => b.parent_block_id === cell.id)
                                            )}
                                        </NormalText>
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            );
        }

        default:
            return null;
    }
};

const SolutionTab: React.FC<{
    featureRequest: FeatureRequest;
}> = ({ featureRequest }) => {
    const translate = useTranslate();
    const documentId = featureRequest.documents.find(
        (doc) => doc.document === "solutionProposal"
    )?.id;

    const { data: solutionProposal, loading, fetchData } = useFetch<MondayDocument>();
    const id = featureRequest.id;

    useEffect(() => {
        if (documentId) {
            fetchData(`/api/feature-requests/${id}/documents/${documentId}`);
        }
    }, [id, documentId, fetchData]);

    if (loading) {
        return (
            <LoadingWrapper>
                <LoadingIndicator isLoading={loading} />
            </LoadingWrapper>
        );
    }

    return (
        <>
            {isEmpty(solutionProposal) ? (
                <EmptyStateHeader>
                    {translate("featureRequests.featureRequestSolutionProposal.missingDescription")}
                </EmptyStateHeader>
            ) : (
                <>
                    <TabHeader>
                        {translate(
                            "featureRequests.featureRequestSolutionProposal.solutionProposal"
                        )}
                    </TabHeader>
                    <DocContainer>
                        {solutionProposal.blocks.map<React.ReactNode>(renderBlock)}
                    </DocContainer>
                </>
            )}
        </>
    );
};

export default SolutionTab;
